import React from "react";
import { Component } from "react";
import authService from "./AuthorizeService";
import { AuthenticationResultStatus } from "./AuthorizeService";
import { QueryParameterNames, LogoutActions, ApplicationPaths } from "./ApiAuthorizationConstants";
import LoginStatus from "./LoginStatus";
import getUrl from "../../functions/getUrl";
import { Redirect } from "react-router";
import snackbarService from "../../services/ui-messaging/SnackbarService";

export interface ILogoutProps {
  action: string;
}

export interface ILogoutState {
  message?: string;
  returnUrl?: string;
  isReady: boolean;
  authenticated: boolean;
}

// the main responsibility of this component is to handle the user's logout process.
// this is the starting point for the logout process, which is usually initiated when a
// user clicks on the logout button on the LoginMenu component.
export class Logout extends Component<ILogoutProps, ILogoutState> {
  constructor(props: ILogoutProps) {
    super(props);

    this.state = {
      returnUrl: undefined,
      message: undefined,
      isReady: false,
      authenticated: false,
    };
  }

  componentDidMount() {
    const action = this.props.action;
    const {
      history: { state },
    } = window;
    switch (action) {
      case LogoutActions.Logout:
        if (!!(state && state.state && state.state.local)) {
          this.logout(this.getReturnUrl(this.state));
        } else {
          // this prevents regular links to <app>/authentication/logout from triggering a logout
          this.setState({
            isReady: true,
            message: "The logout was not initiated from within the page.",
          });
        }
        break;
      case LogoutActions.LogoutCallback:
        this.processLogoutCallback();
        break;
      case LogoutActions.LoggedOut:
        this.setState({
          isReady: true,
          message: "You successfully logged out!",
        });
        break;
      default:
        throw new Error(`Invalid action '${action}'`);
    }

    this.populateAuthenticationState();
  }

  render() {
    const { isReady, message } = this.state;
    if (!isReady) {
      return <div></div>;
    }
    const action = this.props.action;
    switch (action) {
      case LogoutActions.Logout:
      case LogoutActions.LogoutCallback:
        return <LoginStatus message="Logging out..." />;
      case LogoutActions.LoggedOut:
        snackbarService.warn(message || "You successfully logged out");
        return <Redirect to="/" />;
      default:
        throw new Error(`Invalid action '${action}'`);
    }
  }

  async logout(returnUrl: string) {
    const state = { returnUrl };
    const isauthenticated = await authService.isAuthenticated();
    if (isauthenticated) {
      const result = await authService.signOut(state);
      switch (result.status) {
        case AuthenticationResultStatus.Redirect:
          break;
        case AuthenticationResultStatus.Success:
          await this.navigateToReturnUrl(returnUrl);
          break;
        case AuthenticationResultStatus.Fail:
          this.setState({ message: result.message || "Unknown error" });
          break;
        default:
          throw new Error("Invalid authentication result status.");
      }
    } else {
      await this.navigateToReturnUrl(returnUrl);
      // this.setState({ message: "You successfully logged out!" });
    }
  }

  async processLogoutCallback() {
    const url = window.location.href;
    const result = await authService.completeSignOut(url);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        // there should not be any redirects as the only time completeAuthentication finishes
        // is when we are doing a redirect sign in flow.
        throw new Error("Should not redirect.");
      case AuthenticationResultStatus.Success:
        await this.navigateToReturnUrl(this.getReturnUrl(result.state));
        break;
      case AuthenticationResultStatus.Fail:
        this.setState({ message: result.message || "Unknown error" });
        break;
      default:
        throw new Error("Invalid authentication result status.");
    }
  }

  async populateAuthenticationState() {
    const authenticated = await authService.isAuthenticated();
    this.setState({ isReady: true, authenticated });
  }

  getReturnUrl(state: ILogoutState) {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);
    if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
      // this is an extra check to prevent open redirects.
      throw new Error("Invalid return url. The return url needs to have the same origin as the current page.");
    }
    return (state && state.returnUrl) || fromQuery || `${window.location.origin}${getUrl(ApplicationPaths.LoggedOut)}`;
  }

  navigateToReturnUrl(returnUrl: string) {
    return window.location.replace(returnUrl);
  }
}
